/**
 * Public, requester-facing recap (demo #9). The rich web view the recap email
 * links to — "like the status page, but the finished story." Reachable at
 * /recap/:slug with no auth (the opaque slug is the gate, same as the status
 * page); only available once the project is complete.
 *
 * This is a CURATED subset of the internal ProjectRecapResponse — it deliberately
 * omits internal-only fields (budget, actual cost, internal notes, the status
 * audit trail). What the client/PM should see and nothing they shouldn't.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { AddressSchema } from './locations.js';
import { ScheduleDaySchema } from './projects.js';
import { RecapTaskSchema, RecapCrewMemberSchema } from './recap.js';
import { RecapNoteAttachmentSchema } from './recap-notes.js';
export const PublicRecapResponseSchema = z.object({
    // Identity
    project_id: UuidSchema,
    title: z.string(),
    client_name: z.string(),
    sap_order_number: z.string().nullable(),
    // Team (client-facing contacts)
    pm_display_name: z.string().nullable(),
    interior_designer: z.string().nullable(),
    csm: z.string().nullable(),
    sales_executive: z.string().nullable(),
    // Site
    site_address: AddressSchema.nullable(),
    site_contact_name: z.string().nullable(),
    // Schedule
    requested_start_date: IsoDateTimeSchema.nullable(),
    requested_end_date: IsoDateTimeSchema.nullable(),
    approved_schedule: z.array(ScheduleDaySchema).nullable(),
    completed_at: IsoDateTimeSchema.nullable(),
    // What was done
    tasks: z.array(RecapTaskSchema),
    crew_roster: z.array(RecapCrewMemberSchema),
    // Hours — the committed floor we tell the requester, + any overage (man-hours).
    committed_man_minutes: z.number().int().min(0).nullable(),
    overage_man_minutes: z.number().int().min(0),
    total_duration_minutes: z.number().int().min(0),
    total_crewed_minutes: z.number().int().min(0),
    // Quality
    punch_items_total: z.number().int().min(0),
    punch_items_closed: z.number().int().min(0),
    rma_count: z.number().int().min(0),
    // Story — the supervisor's single curated blurb + the photos they chose.
    // Crew note text stays internal; the requester sees only this.
    recap_narrative: z.string().nullable(),
    /** The supervisor-curated, ordered photo gallery — the heart of the recap. */
    recap_gallery: z.array(RecapNoteAttachmentSchema),
    recap_pdf_url: z.string().url().nullable(),
});
//# sourceMappingURL=public-recap.js.map